import { DevicePhoneMobileIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { useEffect } from 'react';

import { ClientButton } from '@/components/UI/Button';
import useRequest from '@/renderer/utils/useRequest';
import type { RequestSchema } from '@/types/request';

// A user's legacy phone numbers, with the SMS-marketing consent on record
// (#1799, made actionable in #1869).
//
// Phones were split out of the (now email-only) contact slots into the dedicated
// `userPhones` table and are preserved ONLY for consent-gated, occasional
// marketing SMS. They are NOT a release-warning / coverage delivery channel, and
// no NEW numbers can be added here.
//
// This section renders ONLY when the user actually has a number on file. Each
// number can be removed, and the consent checkbox is interactive: unchecking it
// records an SMS-marketing opt-out (persisted on the row's `status`). Opting out
// stops future marketing texts; the carrier-standard "reply STOP" path still
// works too. Keeping the opt-in language + disclosures visible documents the
// consent for a Twilio A2P 10DLC registration.
type UserPhone = {
  id: number;
  phone: string;
  verified: boolean;
  verifiedAt: string | null;
  addedAt: string | null;
  status: string | null;
};

const phonesRequest: RequestSchema<UserPhone[]> = {
  name: 'userPhonesOnFile',
  input: '/api/v1/user/phones/me',
  method: 'GET',
};

const removePhoneRequest: RequestSchema<UserPhone[], API.WithData<{ id: number }>> = {
  name: 'removeUserPhone',
  input: '/api/v1/user/phones/remove/me',
  method: 'POST',
};

const consentPhoneRequest: RequestSchema<UserPhone[], API.WithData<{ optOut: boolean }>> = {
  name: 'userPhoneConsent',
  input: '/api/v1/user/phones/consent/me',
  method: 'POST',
};

// Pretty-print an E.164 US/CA number (+1XXXXXXXXXX) as +1 (XXX) XXX-XXXX; leave
// any other shape as-is.
function formatPhone(raw: string): string {
  const digits = `${raw || ''}`.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    const d = digits.slice(1);
    return `+1 (${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  }
  if (digits.length === 10) {
    return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return raw;
}

export default function LegacyPhonesOnFile() {
  // Best-effort lookup: if the user has no numbers on file (or the endpoint
  // 404s), we render nothing — so suppress the global error toast that
  // useRequest would otherwise pop on a non-2xx response.
  const phones = useRequest(phonesRequest, { manual: true, hideAlert: true });
  const removeReq = useRequest(removePhoneRequest, { manual: true });
  const consentReq = useRequest(consentPhoneRequest, { manual: true });

  useEffect(() => {
    phones.run().catch(() => undefined);
  }, []);

  const rows = phones.data ?? [];
  const busy = removeReq.loading || consentReq.loading;

  // Nothing on file (the common case): render nothing so the section stays out
  // of the way for users who never added a phone.
  if (!rows.length) {
    return null;
  }

  const remove = async (id: number) => {
    await removeReq.run({ data: { id } }, { throwOnRun: true }).catch(() => undefined);
    phones.run().catch(() => undefined);
  };

  // Consent is on when a number is verified and the user hasn't opted out.
  const optedOut = rows.some((row) => row.status === 'opted_out');
  const consented = rows.some((row) => row.verified) && !optedOut;

  const toggleConsent = async (nextChecked: boolean) => {
    await consentReq
      .run({ data: { optOut: !nextChecked } }, { throwOnRun: true })
      .catch(() => undefined);
    phones.run().catch(() => undefined);
  };

  return (
    <div className="space-y-3">
      <ul className="space-y-2">
        {rows.map((row) => (
          <li
            key={row.id}
            className="flex max-w-md items-center gap-2 rounded-lg border border-zinc-900/10 border-dashed bg-zinc-50 px-3 py-2 text-sm text-zinc-700 dark:border-white/10 dark:bg-white/5 dark:text-zinc-300"
          >
            <DevicePhoneMobileIcon
              height={20}
              className="size-5 shrink-0 text-zinc-400"
              aria-hidden="true"
            />
            <span className="font-mono">{formatPhone(row.phone)}</span>
            <span className="rounded border border-zinc-900/10 px-1.5 py-0.5 font-mono text-[10px] text-zinc-500 uppercase tracking-wide dark:border-white/10 dark:text-zinc-400">
              SMS · legacy
            </span>
            <ClientButton
              type="button"
              variant="link"
              size="baseline"
              onClick={() => remove(row.id)}
              disabled={busy}
              loading={removeReq.loading}
              className="ml-auto inline-flex items-center gap-1 p-0 font-medium text-zinc-500 no-underline hover:text-red-600 hover:no-underline dark:text-zinc-400 dark:hover:text-red-400"
            >
              <XMarkIcon className="size-4" aria-hidden="true" />
              Remove
            </ClientButton>
          </li>
        ))}
      </ul>
      <p className="text-xs text-zinc-400 dark:text-zinc-500">
        Legacy phone numbers are kept for occasional marketing texts and can&apos;t be added here.
        New recipients are email.
      </p>
      <label className="flex items-start space-x-2 text-xs text-zinc-500 dark:text-zinc-400">
        <input
          type="checkbox"
          checked={consented}
          disabled={busy}
          onChange={(e) => {
            void toggleConsent(e.target.checked);
          }}
          aria-label="Receive RAS marketing text messages"
          className="mt-0.5 size-4 shrink-0 rounded border-zinc-300 dark:border-zinc-600"
        />
        <span>
          I agree to receive RAS marketing text messages at the number
          {rows.length > 1 ? 's' : ''} on file (not release alerts or account updates). Msg
          frequency varies. Msg and data rates may apply. Reply STOP to opt out or HELP for help. I
          have read the{' '}
          <a href="/terms" target="_blank" rel="noreferrer">
            RAS Terms of Service
          </a>{' '}
          and{' '}
          <a href="/privacy" target="_blank" rel="noreferrer">
            Privacy Policy
          </a>
          .
        </span>
      </label>
    </div>
  );
}
